import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash-es/get';
import {
  getCachedVideoURLFromVideoObject,
  getCloudinaryVersionNumber,
} from '../../../common/images';
import utils from '../../../common/utils';
import { ImageContext } from '../../components/Container';

const CloudinaryVideo = props => {
  const imageStatus = React.useContext(ImageContext) || {};
  const videoRef = React.useRef();
  const { shadowStyles, blockStyles, innerStyles } = props;
  const [params, setParams] = React.useState(props.params);
  const [visible, setVisible] = React.useState(props.visible);
  const {
    autoplay,
    mute,
    controls,
    loop,
    publicId,
    uploadingDate,
  } = params.ludus;
  const sources = [];
  const isPlaying = React.useRef(null);
  const [autoMute, setAutoMute] = React.useState(mute);
  const [forcedVersion, setForcedVersion] = React.useState(null);
  const [keyName, setKeyName] = React.useState(
    `${params.canvasId}_${params.ludus.url}`,
  );
  const [isUploading, setIsUploading] = React.useState(true);

  const controlsArray = controls
    ? ['PlayPause', 'Seek', 'Time', 'Volume']
    : null;

  const retryInterval = useRef(0);
  const timeoutId = useRef(null);

  React.useEffect(() => {
    setVisible(props.visible);
    if (videoRef.current && !props.visible) videoRef.current.pause();
    if (videoRef.current && props.visible && autoplay) videoRef.current.play();
  }, [props.visible, videoRef]);

  React.useEffect(() => {
    setParams(props.params);
  }, [props.params]);

  React.useEffect(() => {
    if (publicId && imageStatus[publicId]) {
      setIsUploading(true);
    } else if (!uploadingDate || (Date.now() - uploadingDate) / 1000 > 2) {
      // don't try to play the video too fast after a recent upload finished. The magic cloudinary touch by caching 404...
      setIsUploading(false);
    }
  }, [props.params, imageStatus[publicId]]);

  React.useEffect(() => {
    // force re-render, especially in chrome where it aggressively caches dom elements
    // we must avoid safari(mobile): generates lag
    if (!utils.isSafari) {
      setKeyName(`${keyName}_${autoMute}`);
    }
  }, [autoMute]);
  // Offline
  const localSrc = get(params, 'ludus.localPath');
  if (localSrc) {
    const format = get(params, 'ludus.localFormat');
    sources.push({
      src: localSrc,
      type: `video/${format}`,
    });
  }
  const [thumbnailUrl, setThumbnailUrl] = React.useState(
    get(params, 'ludus.localThumbnail'),
  );

  // Online
  const formats = ['mp4', 'webm'];
  formats.forEach(format => {
    const src = getCachedVideoURLFromVideoObject(params, {
      format,
      forcedVersion,
    });

    sources.push({
      src,
      type: `video/${format}`,
    });
  });

  React.useEffect(() => {
    setKeyName(`${keyName}_${params.width}_${params.height}`);
  }, [props.params]);

  React.useEffect(() => {
    if (!isUploading) {
      setThumbnailUrl(
        getCachedVideoURLFromVideoObject(props.params, {
          format: 'jpg',
        }),
      );
    } else {
      setThumbnailUrl(null);
    }
  }, [props.params, isUploading]);

  const handleOnPlay = e => {
    isPlaying.current = true;
  };

  const handleCanPlay = e => {
    if (!isPlaying.current && !autoMute && autoplay && !mute && visible) {
      setAutoMute(true);
    }
  };

  const handleError = () => {
    // handle cloudinary cache by changing the version
    window.clearTimeout(timeoutId);
    timeoutId.current = window.setTimeout(retryCloudinaryVideo, retryInterval);
  };

  const retryCloudinaryVideo = () => {
    retryInterval.current =
      retryInterval.current > 0 ? retryInterval.current * 2 : 2500;
    setForcedVersion(getCloudinaryVersionNumber());
  };

  if (isUploading) {
    return null;
  }
  return (
    <div style={shadowStyles}>
      <div
        style={{
          ...blockStyles,
          // backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : 'none',
          backgroundSize: 'contain',
        }}
      >
        <video
          ref={videoRef}
          key={keyName} // dynamic change the key to make sure we re-render the video with autoMute change
          width={blockStyles.width}
          height={blockStyles.height}
          style={innerStyles}
          poster={thumbnailUrl !== null ? thumbnailUrl : ''}
          autoPlay={autoplay}
          loop={loop}
          muted={autoMute}
          controls={controlsArray}
          playsInline
          onPlay={handleOnPlay}
          onError={handleError}
          onCanPlayThrough={handleCanPlay}
        >
          {sources.map(source => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
        </video>
      </div>
    </div>
  );
};

CloudinaryVideo.propTypes = {
  blockStyles: PropTypes.objectOf(PropTypes.any),
  shadowStyles: PropTypes.objectOf(PropTypes.any),
  innerStyles: PropTypes.objectOf(PropTypes.any),
  visible: PropTypes.bool,
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.func,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
  ).isRequired,
};
CloudinaryVideo.defaultProps = {
  hyperlink: null,
  matrixGroup: null,
};

export default React.memo(CloudinaryVideo);
